import { useState, useEffect } from "react";
import AppHeader from "./AppHeader.jsx";
import ProfileHeading from "./ProfileHeading.jsx";
import KPIGrid from "./KPIGrid.jsx";
import ConsistencyHeatmap from "./ConsistencyHeatmap.jsx";
import TrendChart from "./TrendChart.jsx";
import MonthlyChart from "./MonthlyChart.jsx";
import MuscleFocus from "./MuscleFocus.jsx";
import ExerciseList from "./ExerciseList.jsx";
import EquipmentBreakdown from "./EquipmentBreakdown.jsx";
import WorkoutLog from "./WorkoutLog.jsx";
import AiReport from "./AiReport.jsx";
import { parseUsername } from "../utils/api.js";
import styles from "./Dashboard.module.css";

const STATUS_LABEL = {
  "loading-preview": "Looking up profile…",
  "loading-history": "Importing workout history…"
};

function comparingLabel(progress) {
  if (progress?.state === "queued" && progress.position > 0) {
    return `In queue · ${progress.position} ahead`;
  }
  return STATUS_LABEL["loading-history"];
}

export default function Dashboard({ id, hidden, state, onPeriodChange, onNewSearch, onStartComparison }) {
  const { username, preview, history, analytics, periodDays, weightUnit, historyWarning, periodWarning, workouts, compare } = state;
  const profile = preview?.normalized?.profile || {};

  const [activeTab, setActiveTab] = useState("overview");
  const [targetWorkoutId, setTargetWorkoutId] = useState(null);
  const [showCompareInput, setShowCompareInput] = useState(false);
  const [compareInput, setCompareInput] = useState("");
  const [compareLocalError, setCompareLocalError] = useState("");
  // The analysis tab costs a provider call, so it only loads once opened — and
  // stays mounted afterwards so switching tabs does not drop the conversation.
  const [aiOpened, setAiOpened] = useState(false);

  const openWorkout = (workoutId) => {
    setActiveTab("workouts");
    setTargetWorkoutId(workoutId);
  };

  function handleCompareOpen() {
    setShowCompareInput(true);
    setCompareInput("");
    setCompareLocalError("");
  }

  function handleCompareSubmit(e) {
    e.preventDefault();
    setCompareLocalError("");
    const parsed = parseUsername(compareInput);
    if (!parsed || parsed.length < 2) {
      setCompareLocalError("Enter a valid Hevy username.");
      return;
    }
    if (parsed.toLowerCase() === username.toLowerCase()) {
      setCompareLocalError("Enter a different username.");
      return;
    }
    onStartComparison(parsed);
  }

  useEffect(() => {
    if (!compare) {
      setShowCompareInput(false);
      setCompareInput("");
      setCompareLocalError("");
    }
  }, [compare]);

  // The dashboard only ever starts the first comparison; the rest are added
  // from the comparison screen itself.
  const pending = compare?.profiles?.[0];
  const isComparing = pending?.status === "loading";
  const compareError = pending?.status === "error" ? pending.error : compareLocalError;

  return (
    <div id={id} className={styles.root} hidden={hidden || undefined}>
      <AppHeader
        username={username}
        periodDays={periodDays}
        onPeriodChange={onPeriodChange}
        onNewSearch={onNewSearch}
        onCompare={!showCompareInput ? handleCompareOpen : undefined}
        showPeriod={activeTab === "overview"}
      />

      <main className={styles.main}>
        <ProfileHeading
          profile={profile}
          username={username}
          analytics={analytics}
          lastWorkout={preview?.normalized?.lastWorkout}
          workouts={workouts}
          pagination={history?.pagination}
        />

        {showCompareInput && (
          <form className={styles.comparePrompt} onSubmit={handleCompareSubmit} noValidate>
            <span className={styles.compareLabel}>Compare with</span>
            <div className={styles.compareInputShell}>
              <input
                className={styles.compareInput}
                type="text"
                value={compareInput}
                onChange={(e) => { setCompareInput(e.target.value); setCompareLocalError(""); }}
                placeholder="@username"
                autoComplete="off"
                spellCheck={false}
                disabled={isComparing}
                autoFocus
              />
            </div>
            <button className={styles.compareSubmit} type="submit" disabled={isComparing || !compareInput.trim()}>
              {isComparing ? comparingLabel(pending?.progress) : "Compare"}
            </button>
            <button
              className={styles.compareCancel}
              type="button"
              onClick={() => { setShowCompareInput(false); setCompareLocalError(""); }}
              disabled={isComparing}
            >
              Cancel
            </button>
            {compareError && (
              <p className={styles.compareError} role="alert">{compareError}</p>
            )}
          </form>
        )}

        <div className={styles.tabBar} role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === "overview"}
            className={`${styles.tab} ${activeTab === "overview" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>
          <button
            role="tab"
            aria-selected={activeTab === "workouts"}
            className={`${styles.tab} ${activeTab === "workouts" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("workouts")}
          >
            Workouts
            {workouts?.length > 0 && (
              <span className={styles.tabCount}>{workouts.length}</span>
            )}
          </button>
          <button
            role="tab"
            aria-selected={activeTab === "analysis"}
            className={`${styles.tab} ${activeTab === "analysis" ? styles.tabActive : ""}`}
            onClick={() => { setActiveTab("analysis"); setAiOpened(true); }}
          >
            Analysis
          </button>
        </div>

        {activeTab === "overview" && (
          <div key="overview" className={styles.tabContent}>
            {periodWarning && (
              <div id="dashboard-status" className={styles.workoutsWarning} role="status">{periodWarning}</div>
            )}
            <KPIGrid analytics={analytics} weightUnit={weightUnit} periodDays={periodDays} />

            <ConsistencyHeatmap consistency={analytics?.consistency} />

            <div className={styles.trendsRow}>
              <TrendChart data={analytics?.weeklySeries || []} />
              <MonthlyChart data={analytics?.monthlySeries || []} />
            </div>

            <MuscleFocus
              buckets={analytics?.muscleBuckets || []}
              prevBuckets={analytics?.previous?.muscleBuckets || []}
              muscleCounts={analytics?.muscleLibraryCounts || []}
              prevMuscleCounts={analytics?.previous?.muscleLibraryCounts || []}
              weeklySeries={analytics?.weeklySeries || []}
              periodDays={periodDays}
            />

            <div className={styles.exerciseRow}>
              <ExerciseList
                exercises={analytics?.exerciseFrequency || []}
                weightUnit={weightUnit}
                period={analytics?.periods?.current}
                workouts={workouts || []}
                onOpenWorkout={openWorkout}
              />
              <EquipmentBreakdown equipment={analytics?.equipmentBreakdown || []} />
            </div>
          </div>
        )}

        {activeTab === "workouts" && (
          <div key="workouts" className={styles.tabContent}>
            {historyWarning && (
              <div id="dashboard-status" className={styles.workoutsWarning} role="status">{historyWarning}</div>
            )}
            <WorkoutLog
              workouts={workouts || []}
              weightUnit={weightUnit}
              targetWorkoutId={targetWorkoutId}
              onTargetConsumed={() => setTargetWorkoutId(null)}
            />
          </div>
        )}

        {aiOpened && (
          <div key="analysis" className={styles.tabContent} hidden={activeTab !== "analysis" || undefined}>
            <AiReport key={username} username={username} />
          </div>
        )}
      </main>
    </div>
  );
}
